// Pure, pay-critical helpers: hour math, period dates, per diems, CSV/export
// shaping, sync-merge logic. No React, no PocketBase — everything here is
// directly unit-testable (calc.test.js).
import { ALL_CODES, EXP_TYPES, PER_DIEM_KEYS, TIME_ACCOUNTS, BANKED_RATE_DEFAULT, BANKED_RATE_STANDBY, BANKED_RATE_DOUBLE } from "./constants";

// --- Hour math ---
export function minutesToTenths(m) {
  const h = Math.floor(m / 60), r = m % 60;
  if (r <= 2 || r >= 57) return h;
  if (r <= 8)  return h + 0.1; if (r <= 14) return h + 0.2;
  if (r <= 20) return h + 0.3; if (r <= 26) return h + 0.4;
  if (r <= 32) return h + 0.5; if (r <= 38) return h + 0.6;
  if (r <= 44) return h + 0.7; if (r <= 50) return h + 0.8;
  return h + 0.9;
}
export function snapHalf(v) { return Math.round(v * 2) / 2; }
export function calcMins(s, e) { if (!s || !e) return 0; let sv = parseFloat(s), ev = parseFloat(e); if (sv >= 24 && ev < sv) ev += 24; else if (ev < sv) ev += 24; const d = ev - sv; return d > 0 ? Math.round(d * 60) : 0; }
export function minsToHrs(m) { return snapHalf(minutesToTenths(m)); }
export function fmtHrs(v) { return ((Math.round((v || 0) * 10) / 10)).toFixed(1); }
export function fmtDur(m) {
  if (!m || m <= 0) return ""; const h = Math.floor(m / 60), mn = m % 60;
  return h > 0 ? (mn > 0 ? `${h}h ${mn}m` : `${h}h`) : `${mn}m`;
}
export function fmtTime(v) {
  if (!v) return "—"; const t = parseFloat(v);
  const h = t >= 24 ? Math.floor(t - 24) : Math.floor(t), m = t % 1 === 0.5 ? "30" : "00";
  return `${h}:${m}`;
}
export function decToTimeStr(v) {
  if (!v) return null; const t = parseFloat(v), r = t >= 24 ? t - 24 : t;
  const h = Math.floor(r), m = r % 1 === 0.5 ? 30 : 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
}
export const isAbs = c => ALL_CODES.find(x => x.value === c)?.type === "absence";
export function entryMins(e) {
  return isAbs(e.attendanceCode)
    ? calcMins(e.duringStart, e.duringEnd)
    : calcMins(e.beforeStart, e.beforeEnd) + calcMins(e.afterStart, e.afterEnd);
}
export function entryHrs(e) { return minsToHrs(entryMins(e)); }

// --- Time-off balances ---
// Banked accrual rate for one entry (0 when it banks nothing): entries saved
// as Paid, absences, and Fire Control (can't be banked) earn nothing; the
// entry's doubleOt flag (second day of rest / stat holiday) overrides the
// code's rate.
export function bankedRate(e) {
  if (!e || isAbs(e.attendanceCode) || e.compWageType !== "1" || e.otCode === "60") return 0;
  if (e.doubleOt) return BANKED_RATE_DOUBLE;
  return e.attendanceCode === "0804" ? BANKED_RATE_STANDBY : BANKED_RATE_DEFAULT;
}
// Pre-check the form's 2× toggle when the date is a Sunday (second day of
// rest on a Mon–Fri week) or the code is 0825 Holiday Worked. Suggestion
// only — the user can toggle freely. Parses as local midnight, not UTC.
export function suggestDoubleOt(iso, code) {
  if (code === "0825") return true;
  if (!iso) return false;
  return new Date(iso + "T00:00:00").getDay() === 0;
}
// The effect of one entry on one balance account: negative hours for a
// mapped absence code, positive banked accrual for Banked OT, or null when
// the entry touches no account.
export function accountDelta(e) {
  if (!e) return null;
  const acct = TIME_ACCOUNTS.find(a => a.deducts.includes(e.attendanceCode));
  if (acct) return { account: acct.key, hours: -entryHrs(e) };
  const rate = bankedRate(e);
  if (rate > 0) return { account: "banked", hours: entryHrs(e) * rate };
  return null;
}
// Running balance for one account: the manually-set anchor plus the effect
// of every entry dated STRICTLY AFTER the anchor date — the number copied
// from ESS already reflects everything up to and including that day.
// Null without a complete anchor: the UI shows "not set", never a guess.
export function accountBalance(accountKey, anchor, entriesByDate) {
  if (!anchor || anchor.hours == null || !anchor.asOf) return null;
  let bal = Number(anchor.hours) || 0;
  Object.entries(entriesByDate || {}).forEach(([date, arr]) => {
    if (date <= anchor.asOf) return;
    (arr || []).forEach(e => {
      const d = accountDelta(e);
      if (d && d.account === accountKey) bal += d.hours;
    });
  });
  return bal;
}
// Banked hours earned vs spent after sinceISO (all entries when null).
export function bankedBreakdown(entriesByDate, sinceISO) {
  let earned = 0, used = 0;
  Object.entries(entriesByDate || {}).forEach(([date, arr]) => {
    if (sinceISO && date <= sinceISO) return;
    (arr || []).forEach(e => {
      const d = accountDelta(e);
      if (!d || d.account !== "banked") return;
      if (d.hours >= 0) earned += d.hours; else used -= d.hours;
    });
  });
  return { earned, used };
}
// Anchor sync healing (same shape as mergeNotes): server wins per account; a
// local anchor that never got a pbId is kept (and pushed) only when the
// server has none for that account. A dirty flag overrides the server copy.
export function mergeTimeAccounts(remote, local) {
  const merged = { ...(remote || {}) };
  const unsynced = [];
  Object.entries(local || {}).forEach(([account, rec]) => {
    if (!rec) return;
    if (!rec.pbId) {
      if (!merged[account]) { merged[account] = rec; unsynced.push({ account, rec }); }
      return;
    }
    if (rec.dirty) { merged[account] = rec; unsynced.push({ account, rec }); }
  });
  return { merged, unsynced };
}

// --- Date helpers ---
// A calendar date in the device's LOCAL timezone. Never use
// toISOString().slice(0,10) for "today": that's the UTC date, which in
// Atlantic time rolls to tomorrow at 8/9pm — exactly when evening OT is logged.
export function localISODate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// Whole days between two ISO dates. Math.round (not floor) so the 23h/25h days
// around a DST change still count as exactly one day.
export function daysBetween(isoA, isoB) {
  return Math.round((new Date(isoB + "T00:00:00") - new Date(isoA + "T00:00:00")) / 86400000);
}
export function addDays(iso, n) { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return localISODate(d); }

// Bi-weekly periods anchored to Sunday 2026-05-03 (payroll period start).
export const ANCHOR = "2026-05-03";
// Which bi-weekly period start does an arbitrary date fall in?
export function periodStartForDate(iso) {
  const n = Math.floor(daysBetween(ANCHOR, iso) / 14);
  return addDays(ANCHOR, n * 14);
}
export function getCurrentPeriodStart() { return periodStartForDate(localISODate()); }
export function stepPeriod(iso, dir) { return addDays(iso, dir * 14); }
export function getPeriodDates(s) { return Array.from({ length: 14 }, (_, i) => addDays(s, i)); }
export function getPeriodEnd(s) { return addDays(s, 13); }

export function dateLbl(iso) {
  const d = new Date(iso + "T00:00:00");
  return {
    weekday: d.toLocaleDateString("en-CA", { weekday: "short" }).toUpperCase(),
    day: d.getDate(),
    month: d.toLocaleDateString("en-CA", { month: "short" }).toUpperCase(),
    dow: d.getDay(),
    full: d.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" }),
  };
}
export function periodRange(s) {
  const a = new Date(s + "T00:00:00"), b = new Date(getPeriodEnd(s) + "T00:00:00");
  return `${a.toLocaleDateString("en-CA", { month: "short", day: "numeric" })} – ${b.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}`;
}
export function timeAgo(ts) {
  if (!ts) return "just now";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60); if (h < 24) return h + "h ago";
  return Math.floor(h / 24) + "d ago";
}

// --- Money / months ---
export function money(n) { return "$" + (Number(n) || 0).toFixed(2); }
export function monthKey(iso) { return (iso || "").slice(0, 7); }
export function monthLabel(iso) { try { return new Date(iso + "T00:00:00").toLocaleDateString("en-CA", { month: "short" }); } catch { return ""; } }
// "June 2026" from a YYYY-MM key.
export function monthLabelLong(key) {
  const [y, m] = (key || "").split("-").map(Number);
  if (!y || !m) return "";
  return new Date(y, m - 1, 1).toLocaleDateString("en-CA", { month: "long", year: "numeric" });
}
// Step a YYYY-MM key by n months.
export function stepMonth(key, n) {
  const [y, m] = (key || "").split("-").map(Number);
  const d = new Date(y, (m - 1) + n, 1);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
}
// First and last calendar day of a YYYY-MM month key.
export function monthFirstISO(key) { return (key || "").slice(0, 7) + "-01"; }
export function monthLastISO(key) {
  const [y, m] = (key || "").split("-").map(Number);
  if (!y || !m) return "";
  return localISODate(new Date(y, m, 0));
}

// --- Admin / auth helpers ---
export const isAdmin = (model) => !!(model && model.admin === true);
export const canDeactivate = (callerId, targetId) => !!callerId && !!targetId && callerId !== targetId;
export function mapAdminError(status) {
  if (status === 401) return "Your session expired — please sign in again.";
  if (status === 403) return "Admin access is required for that.";
  if (status === 400) return "That didn't work — check the email and try again.";
  return "Something went wrong. Please try again.";
}
// Pull a password-reset token out of the URL. The emailed link points at
// https://ottracker.ca/?pwreset=<TOKEN> (set in the PocketBase email template),
// which the SPA reads on load to show the set-password screen.
export function parseResetToken(search) {
  try { return new URLSearchParams(search || "").get("pwreset") || ""; }
  catch { return ""; }
}
// Basic email shape check (UI-side; the server is the real gate).
export function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());
}

// --- Per diems ---
// Expand a per-diem selection into expense records. If all three meals are
// selected the result collapses to one Full Day Meal Per Diem entry; otherwise
// one record per selected meal. Pure → unit-tested.
// `meals` maps a meal key to its amount string; optional `fulldayAmt` overrides
// the combined total when collapsing to full-day.
export function buildPerDiemEntries(meals, shared, idBase, fulldayAmt) {
  if (PER_DIEM_KEYS.every(k => meals[k] != null)) {
    const sum = PER_DIEM_KEYS.reduce((s, k) => s + (parseFloat(meals[k]) || 0), 0);
    const amount = parseFloat(fulldayAmt) > 0 ? parseFloat(fulldayAmt) : sum;
    return [{
      id: String(idBase),
      type: "fullday",
      amount,
      project:  (shared.project  || "").trim(),
      reason:   (shared.reason   || "").trim(),
      location: (shared.location || "").trim(),
      receipt:  shared.receipt   || "",
      date:     shared.date,
    }];
  }
  return PER_DIEM_KEYS
    .filter(m => meals[m] != null)
    .map((m, i) => ({
      id: String(idBase + i),
      type: m,
      amount: parseFloat(meals[m]) || 0,
      project:  (shared.project  || "").trim(),
      reason:   (shared.reason   || "").trim(),
      location: (shared.location || "").trim(),
      receipt:  shared.receipt   || "",
      date:     shared.date,
    }));
}
// `meals` selected and every selected amount is a positive number.
export function perDiemSelectionValid(meals) {
  const keys = Object.keys(meals || {});
  return keys.length > 0 && keys.every(m => parseFloat(meals[m]) > 0);
}

// --- ESS / export shaping ---
// Safe download filename for a receipt image, e.g. receipt-dinner-2026-06-14.jpg
export function receiptFilename(type, date) {
  const t = String(type || "receipt").replace(/[^a-z0-9]/gi, "-").toLowerCase();
  const d = String(date || "").slice(0, 10) || "undated";
  return `receipt-${t}-${d}.jpg`;
}
// Map a month's expense records onto the ESS "Enter Receipts" shape, sorted by
// date. Mileage is flagged separately — in ESS it's entered on a different
// screen (Enter Mileage Details), not as a receipt line.
export function buildESSExpenseRows(monthExpenses) {
  return [...(monthExpenses || [])]
    .map(x => {
      const t = EXP_TYPES[x.type] || EXP_TYPES.other;
      return {
        type: x.type,
        label: t.essLabel || t.label,
        date: (x.date || "").slice(0, 10),
        amount: Number(x.amount) || 0,
        reason: x.reason || "",
        location: x.location || "",
        shortInfo: [x.project, x.reason].filter(Boolean).join(" · "),
        hasReceipt: !!x.receipt,
        receipt: x.receipt || "",
        isMileage: !!(t.isMileage),
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date) || a.label.localeCompare(b.label));
}
// The claim's "Comments" box on ESS General Data: the reason written on each
// day's expenses, laid out one line per calendar day in date order —
// "14.07.2026 — fire response" — ready to paste straight in.
//
// A day's expenses all describe the same day's work (lunch, dinner and a hotel
// on one day carry one reason between them), so the day takes the FIRST
// non-empty reason it has and ignores the rest rather than repeating or
// concatenating. Work that needs a different reason is charged to a different
// cost centre, which is already a separate claim — so a day never legitimately
// needs two. A day with expenses but nothing written yet still gets its bare
// date line, to flag the gap. Mileage rows count: they're entered on a
// different ESS screen, but the day still happened. Pure → unit-tested.
export function buildEssComments(rows) {
  const byDay = new Map(); // ISO date → first non-empty reason (insertion-ordered)
  (rows || []).forEach(r => {
    const date = (r?.date || "").slice(0, 10);
    if (!date) return;
    const reason = (r.reason || "").trim();
    if (!byDay.has(date)) byDay.set(date, "");
    if (reason && !byDay.get(date)) byDay.set(date, reason);
  });
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, reason]) => [essDate(date), reason].filter(Boolean).join(" — "))
    .join("\n");
}
// ESS renders dates as DD.MM.YYYY. Parsed from the ISO parts rather than via
// Date so it can't shift a day across a timezone boundary.
export function essDate(iso) {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-");
  return (y && m && d) ? `${d}.${m}.${y}` : "";
}
// One CSV cell: neutralize spreadsheet formula injection (leading = + - @ or
// tab/CR gets a ' prefix, same policy as the server's sanitize_cell) and apply
// RFC-4180 quoting so commas/quotes/newlines can't shift columns.
export function csvCell(v) {
  let s = v == null ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s) && isNaN(Number(s))) s = "'" + s;
  if (/[",\n\r]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

// --- Sync healing (offline-first merge) ---
// Server is the base; local records that never got a pbId are kept and
// returned for re-push. A local record whose content signature already exists
// on the server for the same date is skipped — that's a create that succeeded
// but lost its pbId write-back (page closed mid-save), and re-pushing it
// would duplicate the record.
// tombstoned: pbIds of records deleted locally but not yet confirmed deleted
// server-side (see storage.js loadPendingDeletes) — filtered out of the
// server pull so an unconfirmed delete doesn't resurrect the record.
export function mergeByDate(remote, local, sigOf, tombstoned) {
  const dead = new Set(tombstoned || []);
  const merged = {};
  Object.entries(remote || {}).forEach(([d, arr]) => {
    merged[d] = (arr || []).filter(r => !dead.has(r.pbId));
  });
  const unsynced = [];
  Object.entries(local || {}).forEach(([date, arr]) => (arr || []).forEach(rec => {
    if (!rec || !rec.id) return;
    if (!rec.pbId) {
      const sig = sigOf(rec);
      if ((merged[date] || []).some(r => sigOf(r) === sig)) return;
      merged[date] = [...(merged[date] || []), rec];
      unsynced.push({ date, rec });
      return;
    }
    // Local record already synced: only a dirty (edited-but-not-yet-pushed)
    // copy overrides the server's version — everything else, server wins.
    if (rec.dirty) {
      const list = merged[date] || [];
      const i = list.findIndex(r => r.pbId === rec.pbId);
      merged[date] = i !== -1 ? [...list.slice(0, i), rec, ...list.slice(i + 1)] : [...list, rec];
      unsynced.push({ date, rec });
    }
  }));
  return { merged, unsynced };
}
export function entrySig(e) {
  return ["attendanceCode", "compWageType", "otCode", "orderProject", "reason",
    "beforeStart", "beforeEnd", "afterStart", "afterEnd", "duringStart", "duringEnd"]
    .map(k => String(e?.[k] ?? "")).join("|") + "|" + (e?.doubleOt ? "2x" : "");
}
export function expenseSig(x) {
  return ["type", "project", "reason", "location", "costCentre"].map(k => String(x?.[k] ?? "")).join("|")
    + "|" + (Number(x?.amount) || 0) + "|" + (x?.receipt ? "r" : "");
}
// Day notes: one per date. Server wins on conflict; a local unsynced note is
// kept (and pushed) only when the server has nothing for that date.
export function mergeNotes(remote, local, tombstoned) {
  const dead = new Set(tombstoned || []);
  const merged = {};
  Object.entries(remote || {}).forEach(([date, n]) => { if (!(n && dead.has(n.pbId))) merged[date] = n; });
  const unsynced = [];
  Object.entries(local || {}).forEach(([date, n]) => {
    if (!n) return;
    if (!n.pbId) {
      if (n.note && !merged[date]) { merged[date] = { note: n.note }; unsynced.push({ date, note: n.note }); }
      return;
    }
    // Local note already synced: only a dirty (edited-but-not-yet-pushed)
    // copy overrides the server's version.
    if (n.dirty) { merged[date] = n; unsynced.push({ date, note: n.note, pbId: n.pbId }); }
  });
  return { merged, unsynced };
}

// --- Images ---
// Trigger a browser download of a data URL. Receipts are base64 JPEGs, so this
// is fully offline — no network or PocketBase round-trip.
export function downloadDataUrl(dataUrl, filename) {
  try {
    const a = document.createElement("a");
    a.href = dataUrl; a.download = filename || "download";
    document.body.appendChild(a); a.click(); a.remove();
  } catch (e) { console.warn("download failed", e); }
}
// Downscale + compress a picked photo to a small JPEG data URL (keeps the DB
// light). Prefers createImageBitmap with imageOrientation:"from-image" so
// EXIF-rotated phone photos don't save sideways; falls back to <img> decode.
export async function resizeImage(file, maxDim = 1200, quality = 0.7) {
  try {
    if (typeof createImageBitmap === "function") {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
      const url = drawToJpeg(bmp, bmp.width, bmp.height, maxDim, quality);
      if (bmp.close) bmp.close();
      return url;
    }
  } catch (_) { /* option unsupported — fall back below */ }
  const img = await loadImageFromFile(file);
  return drawToJpeg(img, img.width, img.height, maxDim, quality);
}
function drawToJpeg(src, width, height, maxDim, quality) {
  let w = width, h = height;
  if (w >= h && w > maxDim) { h = Math.round(h * maxDim / w); w = maxDim; }
  else if (h > w && h > maxDim) { w = Math.round(w * maxDim / h); h = maxDim; }
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(src, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}
function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => resolve(img);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
